import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';

// Import Firebase services and components
import { authService, flashcardService } from './firebase-services.js';
import { enhancedPhoneticsService } from './google-tts-service.js';
import Settings from './Settings.jsx';
import AuthModal from './AuthModal.jsx';



// Text-to-Speech function using Web Speech API with voice selection
const speakText = async (text, type = 'word', voice = null) => {
  try {
    // Try Web Speech API first
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.8;
      utterance.pitch = type === 'letter' ? 1.2 : 1.0;
      
      // Set the voice if provided
      if (voice) {
        utterance.voice = voice;
      }
      
      speechSynthesis.speak(utterance);
      return;
    }
    
    // Fallback: Gemini TTS API (would need actual API key)
    console.log(`TTS: ${text} (${type})`);
    // In production, you would call the Gemini TTS API here
  } catch (error) {
    console.error('TTS Error:', error);
  }
};

// Text-to-Speech function for letter-by-letter pronunciation with enhanced phonetics engine
const speakTextLetterByLetter = async (word, onLetterStart = null, onLetterComplete = null, voice = null) => {
  try {
    const letters = word.split('');
    
    // Check enhanced phonetics service state
    const serviceInitialized = enhancedPhoneticsService && enhancedPhoneticsService.isInitialized();
    
    // Try to use the enhanced phonetics service if available
    if (serviceInitialized) {
      // Use the enhanced phonetics service for better sound quality
      for (let i = 0; i < letters.length; i++) {
        const letter = letters[i];
        
        // Call the start callback immediately to highlight the current letter
        if (onLetterStart) {
          onLetterStart(i);
        }
        
        try {
          // Play the phoneme using the enhanced service
          await enhancedPhoneticsService.playPhoneme(letter, { volume: 0.4 });
        } catch (phonemeError) {
          // If phoneme fails, use the enhanced service's basic fallback
          await enhancedPhoneticsService.basicWebSpeech(letter, { volume: 0.4 });
        }
        
        // Call the completion callback with current letter index
        if (onLetterComplete) {
          onLetterComplete(i + 1);
        }
      }
    } else if ('speechSynthesis' in window) {
      // Final fallback - just speak the letters
      for (let i = 0; i < letters.length; i++) {
        const letter = letters[i];
        
        // Call the start callback immediately to highlight the current letter
        if (onLetterStart) {
          onLetterStart(i);
        }
        
        const utterance = new SpeechSynthesisUtterance(letter);
        utterance.rate = 0.7;
        utterance.pitch = 1.1;
        
        if (voice) {
          utterance.voice = voice;
        }
        
        await new Promise((resolve) => {
          utterance.onend = resolve;
          speechSynthesis.speak(utterance);
        });
        
        if (onLetterComplete) {
          onLetterComplete(i + 1);
        }
        
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
  } catch (error) {
    console.error('Letter-by-letter TTS Error:', error);
  }
};

const App = () => {
  const [user, setUser] = useState(null);
  const [flashcardLists, setFlashcardLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListType, setNewListType] = useState('words');
  const [newListCards, setNewListCards] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAnswer, setShowAnswer] = useState(false);
  const [letterByLetterMode, setLetterByLetterMode] = useState(false);
  const [currentLetterIndex, setCurrentLetterIndex] = useState(0);
  const [completedLetterIndex, setCompletedLetterIndex] = useState(0);
  const [isSpeakingLetterByLetter, setIsSpeakingLetterByLetter] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [readCardAloud, setReadCardAloud] = useState(false); // Master setting, OFF by default
  const [localReadCardAloud, setLocalReadCardAloud] = useState(false); // On-screen toggle
  const [phoneticSoundsEnabled, setPhoneticSoundsEnabled] = useState(false); // Master phonetics setting
  const [isAutoSpeaking, setIsAutoSpeaking] = useState(false); // Track auto-speaking state
  const [showSettings, setShowSettings] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Initialize enhanced phonetics service and load available voices
  useEffect(() => {
    // Initialize the enhanced phonetics service with error handling
    try {
      if (enhancedPhoneticsService && typeof enhancedPhoneticsService.initialize === 'function') {
        enhancedPhoneticsService.initialize().catch(error => {
          console.warn('Failed to initialize enhanced phonetics service:', error);
        });
      }
    } catch (error) {
      console.warn('Enhanced phonetics service not available:', error);
    }
    
    const loadVoices = () => {
      try {
        const voices = speechSynthesis.getVoices();
        setAvailableVoices(voices);
        
        // Select Microsoft Emma as default if available, otherwise prefer English female voices
        const preferredVoice = voices.find(voice => 
          voice.lang.includes('en') && voice.name.includes('Emma')
        ) || voices.find(voice => 
          voice.lang.includes('en') && 
          (voice.name.includes('Female') || voice.name.includes('Samantha') || voice.name.includes('Karen') || voice.name.includes('Moira') || voice.name.includes('Tessa'))
        ) || voices.find(voice => voice.lang.includes('en')) || voices[0];
        
        if (preferredVoice) {
          setSelectedVoice(preferredVoice);
        }
      } catch (error) {
        console.warn('Failed to load voices:', error);
      }
    };

    // Load voices immediately if available
    try {
      if (speechSynthesis.getVoices().length > 0) {
        loadVoices();
      }
    } catch (error) {
      console.warn('Failed to get voices:', error);
    }

    // Listen for voices to be loaded
    try {
      speechSynthesis.addEventListener('voiceschanged', loadVoices);
    } catch (error) {
      console.warn('Failed to add voiceschanged listener:', error);
    }
    
    return () => {
      try {
        speechSynthesis.removeEventListener('voiceschanged', loadVoices);
      } catch (error) {
        console.warn('Failed to remove voiceschanged listener:', error);
      }
    };
  }, []);

  // Load user settings from localStorage
  useEffect(() => {
    if (user && !user.isAnonymous) {
      // Load Read Card Aloud setting
      const savedReadCardAloud = localStorage.getItem(`readCardAloud_${user.uid}`);
      if (savedReadCardAloud !== null) {
        setReadCardAloud(JSON.parse(savedReadCardAloud));
      }

      // Load Phonetic Sounds setting
      const savedPhoneticSounds = localStorage.getItem(`phoneticSounds_${user.uid}`);
      if (savedPhoneticSounds !== null) {
        setPhoneticSoundsEnabled(JSON.parse(savedPhoneticSounds));
      }
    }
  }, [user]);

  // Save Read Card Aloud setting to localStorage
  useEffect(() => {
    if (user && !user.isAnonymous) {
      localStorage.setItem(`readCardAloud_${user.uid}`, JSON.stringify(readCardAloud));
    }
  }, [readCardAloud, user]);

  // Save Phonetic Sounds setting to localStorage
  useEffect(() => {
    if (user && !user.isAnonymous) {
      localStorage.setItem(`phoneticSounds_${user.uid}`, JSON.stringify(phoneticSoundsEnabled));
    }
  }, [phoneticSoundsEnabled, user]);

  // Initialize user session
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(async (user) => {
      if (user) {
        // Check if email is verified for non-anonymous users
        if (!user.isAnonymous && user.email) {
          const isVerified = await authService.isEmailVerified();
          if (!isVerified) {
            // User needs to verify email
            setUser({ ...user, emailVerified: false });
            setLoading(false);
            return;
          }
        }
        
        // Set user state
        setUser({ ...user, emailVerified: user.isAnonymous ? null : true });
        
        // Create baseline lists for all new users (including Google OAuth)
        // Check if user already has lists by doing a quick fetch
        const existingLists = await flashcardService.fetchLists(user.uid);
        if (existingLists.lists.length === 0) {
          console.log('Creating baseline lists for new user:', user.email || user.displayName);
          await createBaselineLists(user.uid);
        }
        
        setLoading(false);
      } else {
        // Show auth modal for user to choose sign-in method
        setShowAuthModal(true);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Handle authentication success
  const handleAuthSuccess = (authUser) => {
    setUser(authUser);
  };

  // Handle sign out
  const handleSignOut = async () => {
    await authService.signOut();
    setUser(null);
    setShowAuthModal(true);
  };

  // Create baseline lists for new users
  const createBaselineLists = async (userId) => {
    const baselineLists = [
      {
        name: 'Uppercase Alphabet',
        type: 'letters',
        cards: 'A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R,S,T,U,V,W,X,Y,Z'.split(',').map(letter => ({ content: letter }))
      },
      {
        name: 'Lowercase Alphabet',
        type: 'letters',
        cards: 'a,b,c,d,e,f,g,h,i,j,k,l,m,n,o,p,q,r,s,t,u,v,w,x,y,z'.split(',').map(letter => ({ content: letter }))
      },
      {
        name: 'Sight Words - Set 1',
        type: 'words',
        cards: 'the,a,and,was,he,for,it,of,with,as,that'.split(',').map(word => ({ content: word }))
      },
      {
        name: 'Sight Words - Set 2',
        type: 'words',
        cards: 'in,is,you,that,he,at,be,this,have,from'.split(',').map(word => ({ content: word }))
      },
      {
        name: 'Sight Words - Set 3',
        type: 'words',
        cards: 'or,had,by,hot,but,not,what,all,we'.split(',').map(word => ({ content: word }))
      },
      {
        name: 'Sight Words - Set 4',
        type: 'words',
        cards: 'can,were,said,use,an,each,which,do,how'.split(',').map(word => ({ content: word }))
      }
    ];

    for (const list of baselineLists) {
      await flashcardService.createList(userId, list);
    }
  };

  // Fetch flashcard lists
  useEffect(() => {
    if (!user) return;

    const unsubscribe = flashcardService.onListsUpdate(user.uid, (lists) => {
      setFlashcardLists(lists);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Create new flashcard list
  const createList = async () => {
    if (!newListName || !newListCards || !user) return;

    try {
      const cards = newListCards
        .split(/[,\\n]+/)
        .map(card => card.trim())
        .filter(card => card.length > 0)
        .map(content => ({ content }));

      const newList = {
        name: newListName,
        type: newListType,
        cards
      };

      const result = await flashcardService.createList(user.uid, newList);
      
      if (result.error) {
        console.error('Error creating list:', result.error);
        alert('Error creating list. Please try again.');
        return;
      }

      // Reset form
      setNewListName('');
      setNewListType('words');
      setNewListCards('');
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating list:', error);
      alert('Error creating list. Please try again.');
    }
  };

  // Delete flashcard list
  const deleteList = async (listId) => {
    if (!user) return;
    
    try {
      const result = await flashcardService.deleteList(user.uid, listId);
      
      if (result.error) {
        console.error('Error deleting list:', result.error);
        alert('Error deleting list. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting list:', error);
      alert('Error deleting list. Please try again.');
    }
  };

  // Navigation functions
  const nextCard = () => {
    if (selectedList && currentCardIndex < selectedList.cards.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
      setShowAnswer(false);
    }
  };

  const prevCard = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(currentCardIndex - 1);
      setShowAnswer(false);
    }
  };

  // Activate AudioContext on user gesture
  const activateAudioContext = useCallback(async () => {
    // The enhanced service handles AudioContext internally
  }, []);

  // Speak current card
  const speakCurrentCard = useCallback((isAuto = false) => {
    if (!selectedList || !selectedList.cards[currentCardIndex]) return;
    
    const card = selectedList.cards[currentCardIndex];
    
    // Set auto-speaking state for visual feedback
    if (isAuto) {
      setIsAutoSpeaking(true);
    }
    
    // First, try to activate audio if needed
    if (enhancedPhoneticsService && enhancedPhoneticsService.isInitialized() && !isAuto) {
      activateAudioContext();
    }
    
    if (letterByLetterMode && selectedList.type === 'words') {
      // Letter-by-letter mode for words
      setCurrentLetterIndex(0);
      setCompletedLetterIndex(0);
      setIsSpeakingLetterByLetter(true);
      speakTextLetterByLetter(
        card.content, 
        (letterIndex) => {
          // Highlight current letter immediately
          setCurrentLetterIndex(letterIndex);
        },
        (letterIndex) => {
          // Mark as completed
          setCompletedLetterIndex(letterIndex);
        },
        selectedVoice
      ).then(() => {
        setIsSpeakingLetterByLetter(false);
        setCurrentLetterIndex(0);
        setCompletedLetterIndex(0);
        setIsAutoSpeaking(false);
      });
    } else {
      // Normal speaking mode
      const type = selectedList.type === 'letters' ? 'letter' : 'word';
      
      // Create utterance with end event handler
      const utterance = new SpeechSynthesisUtterance(card.content);
      utterance.rate = 0.8;
      utterance.pitch = type === 'letter' ? 1.2 : 1.0;
      
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }
      
      utterance.onend = () => {
        setIsAutoSpeaking(false);
      };
      
      speechSynthesis.speak(utterance);
    }
  }, [selectedList, currentCardIndex, letterByLetterMode, selectedVoice, activateAudioContext]);

  // Auto-speak when card changes - use refs to prevent multiple triggers
  const lastCardRef = useRef({ index: -1, listId: null });
  
  useEffect(() => {
    const currentCardId = `${selectedList?.id || ''}-${currentCardIndex}`;
    const lastCardId = `${lastCardRef.current.listId || ''}-${lastCardRef.current.index}`;
    
    if (selectedList && localReadCardAloud && currentCardId !== lastCardId) {
      // Update ref to prevent re-triggering
      lastCardRef.current = { index: currentCardIndex, listId: selectedList.id };
      
      // Stop any current speech
      if ('speechSynthesis' in window) {
        speechSynthesis.cancel();
      }
      
      // Reset letter-by-letter state when changing cards
      setCurrentLetterIndex(0);
      setCompletedLetterIndex(0);
      setIsSpeakingLetterByLetter(false);
      setIsAutoSpeaking(false);
      
      // Speak the card after a short delay to ensure state is settled
      const timer = setTimeout(() => {
        speakCurrentCard(true);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [currentCardIndex, selectedList?.id, localReadCardAloud, speakCurrentCard]);

  // Stop voice when page changes or unmounts
  useEffect(() => {
    return () => {
      if ('speechSynthesis' in window) {
        speechSynthesis.cancel();
      }
    };
  }, []);

  // Sync local toggle with master setting when list changes
  useEffect(() => {
    if (selectedList) {
      setLocalReadCardAloud(readCardAloud);
      // Only set phonetics mode for word lists
      if (selectedList.type === 'words') {
        setLetterByLetterMode(phoneticSoundsEnabled);
      } else {
        setLetterByLetterMode(false);
      }
    }
  }, [readCardAloud, phoneticSoundsEnabled, selectedList]);

  // Reset letter-by-letter mode when switching lists
  useEffect(() => {
    // Stop any ongoing speech
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
    
    // Reset visual state but keep the mode based on list type and master setting
    setCurrentLetterIndex(0);
    setCompletedLetterIndex(0);
    setIsSpeakingLetterByLetter(false);
    
    // Set phonetics mode based on list type and master setting
    if (selectedList && selectedList.type === 'words') {
      setLetterByLetterMode(phoneticSoundsEnabled);
    } else {
      setLetterByLetterMode(false);
    }
  }, [selectedList, phoneticSoundsEnabled]);

  // Stop speech when navigating away from cards
  useEffect(() => {
    if (!selectedList && 'speechSynthesis' in window) {
      speechSynthesis.cancel();
      setIsAutoSpeaking(false);
    }
  }, [selectedList]);

  // Cleanup auto-speaking state when toggle is turned off
  useEffect(() => {
    if (!localReadCardAloud && 'speechSynthesis' in window) {
      speechSynthesis.cancel();
      setIsAutoSpeaking(false);
    }
  }, [localReadCardAloud]);

  const currentCard = selectedList?.cards[currentCardIndex];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-4xl font-kindergarten text-primary-600 animate-pulse-slow">
          Loading flashcards...
        </div>
      </div>
    );
  }

  if (selectedList) {
    return (
      <div className="min-h-screen p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-6xl font-kindergarten text-primary-600 mb-2">
              {selectedList.name}
            </h1>
            <p className="text-xl text-gray-600">
              Card {currentCardIndex + 1} of {selectedList.cards.length}
            </p>
          </div>

          {/* Flashcard */}
          <div className="flashcard mb-8 min-h-[400px] flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl md:text-8xl font-kindergarten text-primary-700 mb-4">
                {showAnswer && currentCard.answer ? currentCard.answer : currentCard.content}
                {letterByLetterMode && selectedList.type === 'words' && currentCard.content && (
                  <div className="mt-4">
                    {currentCard.content.split('').map((letter, index) => (
                      <span
                        key={index}
                        className={`inline-block mx-2 text-4xl md:text-6xl transition-all duration-150 transform ${
                          index === currentLetterIndex
                            ? 'text-yellow-500 scale-125 animate-pulse shadow-lg bg-yellow-100 rounded-lg px-3 py-2'
                            : index < completedLetterIndex
                            ? 'text-green-500 scale-110'
                            : 'text-gray-400 scale-100'
                        }`}
                      >
                        {letter}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {selectedList.type === 'math' && currentCard.answer && (
                <button
                  onClick={() => setShowAnswer(!showAnswer)}
                  className="btn-secondary mb-4"
                >
                  {showAnswer ? 'Show Problem' : 'Show Answer'}
                </button>
              )}
            </div>
          </div>

          {/* Read Card Aloud Toggle */}
          <div className="mb-6 bg-white rounded-2xl p-4 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-lg font-kindergarten text-gray-700">
                  🔊 Read Card Aloud
                </span>
                {isAutoSpeaking && !letterByLetterMode && (
                  <span className="text-sm text-green-500 animate-pulse">
                    Reading...
                  </span>
                )}
              </div>
              <button
                onClick={() => {
                  activateAudioContext();
                  setLocalReadCardAloud(!localReadCardAloud);
                }}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                  localReadCardAloud ? 'bg-primary-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform duration-200 ${
                    localReadCardAloud ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <div className="mt-3 text-sm text-gray-600 font-kindergarten">
              {localReadCardAloud ? (
                <span>
                  Cards will be read aloud automatically. 
                  {letterByLetterMode && selectedList.type === 'words' 
                    ? " Phonetic sounds will be used for words." 
                    : selectedList.type === 'letters' 
                    ? " Letter names will be spoken." 
                    : " Words will be spoken normally."}
                </span>
              ) : (
                <span>Turn on to have cards read aloud automatically when displayed.</span>
              )}
            </div>
          </div>

          {/* Letter-by-Letter Toggle (only for word lists) */}
          {selectedList.type === 'words' && (
            <div className="mb-6 bg-white rounded-2xl p-4 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-lg font-kindergarten text-gray-700">
                    🔊 Phonetic Sounds Mode
                  </span>
                  {isSpeakingLetterByLetter && (
                    <span className="text-sm text-secondary-500 animate-pulse">
                      Sounding out...
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    activateAudioContext();
                    setLetterByLetterMode(!letterByLetterMode);
                  }}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                    letterByLetterMode ? 'bg-primary-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform duration-200 ${
                      letterByLetterMode ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {letterByLetterMode && (
                <div className="mt-3 text-sm text-gray-600 font-kindergarten">
                  Letters will sound out their phonetic sounds using authentic kindergarten phonics!
                  {enhancedPhoneticsService.isInitialized() ? " ✨ Enhanced phonetics service" : " 📢 High-quality speech"}
                </div>
              )}
            </div>
          )}

          {/* Controls */}
          <div className="flex flex-col items-center space-y-4">
            <div className="flex space-x-4">
              <button
                onClick={() => {
                  activateAudioContext();
                  prevCard();
                }}
                disabled={currentCardIndex === 0}
                className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>
              <button
                onClick={() => {
                  activateAudioContext();
                  speakCurrentCard();
                }}
                className="btn-primary"
              >
                🔊 Sound It Out
              </button>
              <button
                onClick={() => {
                  activateAudioContext();
                  nextCard();
                }}
                disabled={currentCardIndex === selectedList.cards.length - 1}
                className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
            <button
              onClick={() => {
                activateAudioContext();
                setSelectedList(null);
                setCurrentCardIndex(0);
                setShowAnswer(false);
              }}
              className="btn-secondary"
            >
              Back to Lists
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center space-x-2">
              {user && (
                <div className="flex items-center space-x-3 bg-white rounded-full shadow-lg px-4 py-3">
                  <div className="text-sm text-gray-600">
                    {user.isAnonymous ? 'Guest' : user.email || user.displayName}
                  </div>
                  {!user.isAnonymous ? (
                    <button
                      onClick={handleSignOut}
                      className="text-red-500 hover:text-red-700 text-sm font-kindergarten font-bold px-3 py-1 border-2 border-red-300 rounded-xl hover:bg-red-50 transition-colors"
                    >
                      Sign Out
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowAuthModal(true)}
                      className="btn-primary text-sm px-3 py-1"
                    >
                      Sign In
                    </button>
                  )}
                </div>
              )}
              {!user && (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="btn-primary text-lg px-6 py-3"
                >
                  Sign In
                </button>
              )}
            </div>
            <button
              onClick={() => setShowSettings(true)}
              className="p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-shadow duration-200"
              title="Settings"
            >
              ⚙️
            </button>
          </div>
          <h1 className="text-5xl md:text-7xl font-kindergarten text-primary-600 mb-4">
            🎒 Kindergarten Flashcards
          </h1>
          <p className="text-xl md:text-2xl text-gray-600">
            Learn letters, words, and more!
          </p>
        </div>


        {/* Email Verification Notice */}
        {user && !user.isAnonymous && user.emailVerified === false && (
          <div className="mb-6 bg-orange-50 border-4 border-orange-300 rounded-2xl p-4 text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <span className="text-2xl">📧</span>
              <span className="text-lg font-kindergarten text-orange-800 font-bold">
                Verify Your Email
              </span>
            </div>
            <p className="text-sm text-orange-700 font-kindergarten mb-3">
              Please check your email and click the verification link to fully activate your account.
            </p>
            <button 
              onClick={async () => {
                const result = await authService.sendEmailVerification();
                if (result.error) {
                  alert('Failed to send verification email. Please try again.');
                } else {
                  alert('Verification email sent! Please check your inbox.');
                }
              }}
              className="btn-primary text-sm px-4 py-2"
            >
              Resend Verification Email
            </button>
          </div>
        )}

        {/* Guest Mode Notice */}
        {user && user.isAnonymous && (
          <div className="mb-6 bg-yellow-50 border-4 border-yellow-300 rounded-2xl p-4 text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <span className="text-2xl">💡</span>
              <span className="text-lg font-kindergarten text-yellow-800 font-bold">
                Guest Mode
              </span>
            </div>
            <p className="text-sm text-yellow-700 font-kindergarten">
              Your flashcards won't be saved permanently. 
              <button 
                onClick={() => setShowAuthModal(true)}
                className="text-yellow-800 underline hover:no-underline font-bold"
              >
                Sign in
              </button> 
              to keep your progress!
            </p>
          </div>
        )}

        {/* Create Button */}
        <div className="text-center mb-8">
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary text-xl"
          >
            ➕ Create New List
          </button>
        </div>

        {/* Flashcard Lists */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {flashcardLists.map((list) => (
            <div
              key={list.id}
              className="flashcard cursor-pointer hover:shadow-3xl"
              onClick={() => setSelectedList(list)}
            >
              <div className="text-center">
                <h3 className="text-2xl font-kindergarten text-primary-700 mb-2">
                  {list.name}
                </h3>
                <p className="text-gray-600 mb-4">
                  {list.cards.length} cards • {list.type}
                </p>
                <div className="flex justify-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedList(list);
                    }}
                    className="btn-primary text-sm px-4 py-2"
                  >
                    Start
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteList(list.id);
                    }}
                    className="btn-secondary text-sm px-4 py-2"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl p-6 md:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-3xl font-kindergarten text-primary-700 mb-6">
                Create New Flashcard List
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-lg font-kindergarten text-gray-700 mb-2">
                    List Name
                  </label>
                  <input
                    type="text"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    className="w-full px-4 py-3 border-4 border-primary-300 rounded-2xl text-lg focus:border-primary-500 focus:outline-none"
                    placeholder="e.g., Sight Words Week 1"
                  />
                </div>

                <div>
                  <label className="block text-lg font-kindergarten text-gray-700 mb-2">
                    Type
                  </label>
                  <select
                    value={newListType}
                    onChange={(e) => setNewListType(e.target.value)}
                    className="w-full px-4 py-3 border-4 border-primary-300 rounded-2xl text-lg focus:border-primary-500 focus:outline-none"
                  >
                    <option value="words">Sight Words</option>
                    <option value="letters">Letters</option>
                    <option value="math">Math Problems</option>
                  </select>
                </div>

                <div>
                  <label className="block text-lg font-kindergarten text-gray-700 mb-2">
                    Cards (one per line or separated by commas)
                  </label>
                  <textarea
                    value={newListCards}
                    onChange={(e) => setNewListCards(e.target.value)}
                    className="w-full px-4 py-3 border-4 border-primary-300 rounded-2xl text-lg focus:border-primary-500 focus:outline-none h-32"
                    placeholder={newListType === 'math' ? '2 + 3&#10;5 - 1&#10;4 x 2' : 'the&#10;a&#10;is&#10;can'}
                  />
                </div>
              </div>

              <div className="flex space-x-4 mt-6">
                <button
                  onClick={createList}
                  className="btn-primary flex-1"
                >
                  Create List
                </button>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewListName('');
                    setNewListType('words');
                    setNewListCards('');
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {showSettings && (
          <Settings
            onClose={() => setShowSettings(false)}
            selectedVoice={selectedVoice}
            onVoiceChange={setSelectedVoice}
            readCardAloud={readCardAloud}
            onReadCardAloudChange={setReadCardAloud}
            phoneticSoundsEnabled={phoneticSoundsEnabled}
            onPhoneticSoundsChange={setPhoneticSoundsEnabled}
          />
        )}

        {/* Authentication Modal */}
        {showAuthModal && (
          <AuthModal
            onClose={() => setShowAuthModal(false)}
            onAuthSuccess={handleAuthSuccess}
            current_user={user}
          />
        )}
      </div>
    </div>
  );
};

// Render the app
const container = document.getElementById('root');
const root = createRoot(container);
root.render(<App />);
